/**
 * AI:
 * Status: 'confirmed'
 * Type: Component
 * SubType: Presentational
 * Reason: Details tab of the EI tooling area. Shows the parent-import card,
 *         record meta-info (timestamps, counters), editable review note and
 *         the main actions (resume pipeline, re-lookup, mark irrelevant).
 */
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';
import {Router} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {EiImportRecordDto} from '../../../../../../../../model/dto/entity/ei-import-record.dto';
import {EiImportDto} from '../../../../../../../../model/dto/entity/ei-import.dto';
import {EiImportPhase} from '../../../../../../../../enum/ei-import-phase.enum';
import {EiImportExecutionStatus} from '../../../../../../../../enum/ei-import-execution-status.enum';
import {EiToolingAction} from '../../../ei-dashboard-tooling-area.component';

@Component({
    selector: 'ei-tooling-details-tab',
    templateUrl: './ei-tooling-details-tab.component.html',
    styleUrls: ['./ei-tooling-details-tab.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EiToolingDetailsTabComponent implements OnChanges {

    @Input() record: EiImportRecordDto | null = null;
    @Input() parentImport: EiImportDto | null = null;

    @Output() onAction: EventEmitter<EiToolingAction> = new EventEmitter<EiToolingAction>();

    /** Locally editable review-note buffer. */
    editableNote: string = '';

    constructor(
        private readonly router: Router,
        private readonly confirmationService: ConfirmationService,
        private readonly cdr: ChangeDetectorRef,
    ) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['record']) {
            this.editableNote = this.record?.reviewNote ?? '';
            this.cdr.markForCheck();
        }
    }

    /**
     * Returns a short German label for the parent import's phase.
     */
    get phaseLabel(): string {
        if (!this.parentImport || this.parentImport.phase == null) {
            return 'Phase unbekannt';
        }
        switch (this.parentImport.phase) {
            case EiImportPhase.INGESTION: return 'Ingestion';
            case EiImportPhase.OBJECT_RESOLUTION: return 'Objekt-Auflösung';
            case EiImportPhase.OBJECT_REVIEW: return 'Objekt-Prüfung';
            case EiImportPhase.VALIDATION: return 'Validierung';
            case EiImportPhase.PERSISTENCE: return 'Persistenz';
            case EiImportPhase.POST_PROCESSING: return 'Nachbearbeitung';
            case EiImportPhase.DONE: return 'Abgeschlossen';
            default: return 'Phase unbekannt';
        }
    }

    /**
     * Returns a German label for the execution status.
     */
    get executionStatusLabel(): string {
        if (!this.parentImport || this.parentImport.executionStatus == null) {
            return 'Unbekannt';
        }
        switch (this.parentImport.executionStatus) {
            case EiImportExecutionStatus.IDLE: return 'Bereit';
            case EiImportExecutionStatus.RUNNING: return 'Läuft';
            case EiImportExecutionStatus.FINISHED: return 'Abgeschlossen';
            case EiImportExecutionStatus.ERROR: return 'Fehler';
            default: return 'Unbekannt';
        }
    }

    /**
     * Navigates to the parent import page (relative to the app root).
     */
    navigateToImport(): void {
        if (!this.parentImport?.id) {
            return;
        }
        this.router.navigate(['ei', 'imports', this.parentImport.id]);
    }

    /**
     * Emits the save-note action to the parent shell.
     */
    saveNote(): void {
        this.onAction.emit({type: 'saveNote', payload: {note: this.editableNote ?? ''}});
    }

    /**
     * Asks the operator to confirm + enter a reason before marking irrelevant.
     */
    confirmMarkIrrelevant(): void {
        this.confirmationService.confirm({
            header: 'Als nicht relevant markieren',
            message:
                'Soll dieser Datensatz als nicht relevant markiert werden? ' +
                'Er wird dann von der Freigabeprüfung ausgeschlossen.',
            icon: 'fa-regular fa-ban',
            acceptLabel: 'Nicht relevant',
            rejectLabel: 'Abbrechen',
            acceptButtonStyleClass: 'p-button-warning',
            rejectButtonStyleClass: 'p-button-text p-button-secondary',
            accept: () => {
                this.onAction.emit({type: 'markIrrelevant', payload: {note: this.editableNote ?? ''}});
            },
        });
    }

    /**
     * Emits a resume event after confirmation (fast path; no free-text reason).
     */
    confirmResume(): void {
        this.confirmationService.confirm({
            header: 'Pipeline fortsetzen',
            message: 'Soll die Pipeline für diesen Datensatz fortgesetzt werden?',
            icon: 'fa-regular fa-play',
            acceptLabel: 'Fortsetzen',
            rejectLabel: 'Abbrechen',
            acceptButtonStyleClass: 'p-button-primary',
            rejectButtonStyleClass: 'p-button-text p-button-secondary',
            accept: () => this.onAction.emit({type: 'resume'})
        });
    }

    /**
     * Emits a re-lookup event after confirmation.
     */
    confirmReLookup(): void {
        this.confirmationService.confirm({
            header: 'Erneut auflösen',
            message: 'Soll dieser Datensatz erneut aufgelöst werden? Bestehende Kandidaten werden neu berechnet.',
            icon: 'fa-regular fa-rotate',
            acceptLabel: 'Erneut auflösen',
            rejectLabel: 'Abbrechen',
            acceptButtonStyleClass: 'p-button-primary',
            rejectButtonStyleClass: 'p-button-text p-button-secondary',
            accept: () => this.onAction.emit({type: 'reLookup'})
        });
    }

    /** Disable the save-button when nothing changed. */
    get noteIsUnchanged(): boolean {
        const current = this.record?.reviewNote ?? '';
        return (this.editableNote ?? '') === current;
    }
}
